import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

/**
 * Iter-158.0 — Department workspace placeholder.
 *
 * First iteration: no department-specific tooling. Shows the shared
 * script status, the shared scene count, the shared character count,
 * plus the full lists — proving that every department reads from the
 * same source of truth. Department-specific tooling arrives in later
 * iterations.
 */
export default function BetaDepartment() {
    const { projectId, deptKey } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/beta/projects/${projectId}/departments/${deptKey}`);
            setData(data);
        } catch (e) {
            toast.error(formatApiError(e));
            if (e?.response?.status === 404) nav(`/beta/projects/${projectId}`);
        } finally {
            setLoading(false);
        }
    }, [projectId, deptKey, nav]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    if (loading) {
        return <div className="min-h-screen bg-neutral-950 text-neutral-500 p-8 text-sm">Loading…</div>;
    }
    if (!data) return null;

    return (
        <div data-testid="beta-department" className="min-h-screen bg-neutral-950 text-neutral-100 px-4 py-6 sm:px-8 sm:py-10">
            <header className="max-w-3xl mx-auto mb-6">
                <Link
                    to={`/beta/projects/${projectId}`}
                    className="tap text-[11px] uppercase tracking-[0.2em] text-neutral-400 hover:text-neutral-200"
                >
                    ← {data.project?.name}
                </Link>
                <p className="text-[11px] uppercase tracking-[0.24em] text-amber-300/80 mt-2">Department workspace</p>
                <h1 data-testid="beta-department-label" className="text-2xl sm:text-3xl font-semibold mt-1">
                    {data.department?.label}
                </h1>
            </header>

            <div className="max-w-3xl mx-auto space-y-4">
                <section className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4 grid grid-cols-3 gap-3">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Shared script</p>
                        <p data-testid="beta-department-script-title" className="text-sm text-neutral-100 mt-1 truncate">
                            {data.script?.title || "—"}
                        </p>
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Shared scenes</p>
                        <p data-testid="beta-department-scene-count" className="text-lg text-neutral-100 mt-1">{data.scene_count}</p>
                    </div>
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.2em] text-neutral-500">Shared characters</p>
                        <p data-testid="beta-department-character-count" className="text-lg text-neutral-100 mt-1">{data.character_count}</p>
                    </div>
                </section>

                <section className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4">
                    <h2 className="text-[11px] uppercase tracking-[0.24em] text-neutral-400">Scenes</h2>
                    {data.scenes?.length ? (
                        <ul data-testid="beta-department-scene-list" className="mt-3 space-y-1">
                            {data.scenes.map((s) => (
                                <li key={s.id} className="text-[13px] text-neutral-200 border-b border-neutral-800/60 py-1.5 last:border-b-0">
                                    <span className="text-neutral-500 text-[11px] uppercase tracking-[0.2em] mr-2">
                                        {s.number ? `#${s.number}` : ""}
                                    </span>
                                    <span className={s.omitted ? "text-amber-300/70" : ""}>{s.heading}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-2 text-sm text-neutral-500">No scenes yet — upload a script on the project screen.</p>
                    )}
                </section>

                <section className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-4">
                    <h2 className="text-[11px] uppercase tracking-[0.24em] text-neutral-400">Characters</h2>
                    {data.characters?.length ? (
                        <ul data-testid="beta-department-character-list" className="mt-3 flex flex-wrap gap-1.5">
                            {data.characters.map((c) => (
                                <li key={c.id} className="text-[12px] text-neutral-200 border border-neutral-800 rounded-full px-2.5 py-1">
                                    {c.name}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="mt-2 text-sm text-neutral-500">No characters yet.</p>
                    )}
                </section>

                <p className="text-[11px] text-neutral-500 pt-2">
                    This is a scaffold view. Department-specific tooling (locations, action props, costume notes, VFX shots) will land in later iterations.
                </p>
            </div>
        </div>
    );
}
